import { AppLayout } from "@/components/AppLayout";
import * as reboot_api from "@reboot-dev/reboot-api";
import { useSignOutFully } from "../hooks/useSignOutFully";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useOrganization } from "../gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";
import { User_Status } from "../gen/rbt/cloud/v1alpha1/user/user_pb";
import { User, useUser } from "../gen/rbt/cloud/v1alpha1/user/user_rbt_react";
import { useAuthenticatedSession } from "../hooks/useAuthenticatedSession";

interface AuthenticatedContextType {
  userId: string;
  userEmail: string;
  userState: User.State | undefined;
  currentOrgId: string | undefined;
  setCurrentOrgId: (id: string) => void;
  organizationIds: string[];
}

const AuthenticatedContext = createContext<AuthenticatedContextType | null>(
  null
);

export function useAuthenticatedContext() {
  const context = useContext(AuthenticatedContext);
  if (!context) {
    throw new Error(
      "useAuthenticatedContext must be used within AuthenticatedLayout"
    );
  }
  return context;
}

function RedirectToOnboarding() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate("/onboarding", { replace: true });
  }, [navigate]);
  return <></>;
}

// Gates access to the app based on payment status.
function PaymentGuard({
  organizationId,
  children,
}: {
  organizationId: string;
  children: React.ReactNode;
}) {
  const { useSubscription } = useOrganization({
    id: organizationId,
  });
  const { response, aborted } = useSubscription();

  // Organization creation is a transaction that always creates a
  // subscription, so if the org exists this should be unreachable.
  // Redirect to onboarding in the unlikely event this happens.
  if (aborted !== undefined) {
    return <RedirectToOnboarding />;
  }

  // Still loading.
  if (!response) {
    return <></>;
  }

  // Billing-exempt organizations don't need a payment method — allow
  // through.
  if (response.billingExempt) {
    return <>{children}</>;
  }

  // Payment method exists — allow through.
  if (response.subscription?.paymentMethodId) {
    return <>{children}</>;
  }

  // No payment method — send to onboarding to complete setup.
  return <RedirectToOnboarding />;
}

export function AuthenticatedLayout() {
  const session = useAuthenticatedSession();

  if (!session) {
    return <></>;
  }

  return <AuthenticatedLayoutInner {...session} />;
}

function AuthenticatedLayoutInner({ userId }: { userId: string }) {
  const { useGet, setLastSelectedOrganization } = useUser({
    id: userId,
  });
  const { response, aborted } = useGet();
  const navigate = useNavigate();
  const signOut = useSignOutFully();

  const userState = response?.userState;
  const userEmail = userState?.email ?? "";
  const rawOrganizationIds = userState?.organizationIds ?? [];

  // Stabilize the array reference — only produce a new array
  // when the actual IDs change, not on every `useGet` response.
  const orgIdsKey = rawOrganizationIds.join(",");
  const organizationIds = useMemo(
    () => rawOrganizationIds,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgIdsKey]
  );

  // Derive currentOrgId from pending (optimistic) or committed state.
  const currentOrgId =
    setLastSelectedOrganization.pending?.[0]?.request.organizationId ||
    userState?.lastSelectedOrganizationId ||
    organizationIds[0];

  // Stable callback — avoids cascading re-renders through
  // context and `AppLayout` props.
  const handleOrganizationChange = useCallback(
    async (organizationId: string) => {
      await setLastSelectedOrganization({ organizationId });
    },
    [setLastSelectedOrganization]
  );

  // Keep `userState` in a ref so it does not force the
  // context value to change on every `useGet` response.
  // No current consumer reads `userState` from context
  // (`SettingsPage` uses only primitive-derived fields);
  // `AppLayout` receives it as a direct prop below.
  const userStateRef = useRef(userState);
  userStateRef.current = userState;

  // Memoize so context consumers only re-render when one
  // of the primitive-valued fields actually changes.
  const contextValue = useMemo<AuthenticatedContextType>(
    () => ({
      userId,
      userEmail,
      userState: userStateRef.current,
      currentOrgId,
      setCurrentOrgId: handleOrganizationChange,
      organizationIds,
    }),
    [userId, userEmail, currentOrgId, handleOrganizationChange, organizationIds]
  );

  // Handle errors from User.Get.
  useEffect(() => {
    if (aborted !== undefined) {
      if (
        aborted.error instanceof reboot_api.errors_pb.StateNotConstructed ||
        aborted.error instanceof reboot_api.errors_pb.Unauthenticated
      ) {
        // User doesn't exist yet — redirect to onboarding to create them.
        navigate("/onboarding", { replace: true });
      }
    }
  }, [aborted, navigate]);

  if (response == undefined) {
    return <></>;
  }

  // Redirect to onboarding if user isn't fully set up.
  const needsOnboarding =
    userState?.status !== User_Status.ACTIVE || // Not active (waitlisted).
    organizationIds.length === 0; // No organizations.

  if (needsOnboarding) {
    return <RedirectToOnboarding />;
  }

  return (
    <AuthenticatedContext.Provider value={contextValue}>
      <PaymentGuard organizationId={currentOrgId!}>
        <AppLayout
          userState={userState}
          onSignOut={() => void signOut()}
          organizationIds={organizationIds}
          currentOrganizationId={currentOrgId}
          onOrganizationChange={handleOrganizationChange}
        >
          <Outlet />
        </AppLayout>
      </PaymentGuard>
    </AuthenticatedContext.Provider>
  );
}
